import { useEffect, useState } from "react";
import styles from "./Stores.module.scss";
import ProductsService from "../../../api/services/products.service";
import Loading from "../../../components/common/loading/Loading";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import { StoresTypes } from "../../../api/types/products.types";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../../../components/common/localized-link/LocalizedLink";
import { useCurrencies } from "../../../context/CurrenciesContext";
import Button from "../../../components/common/button/Button";
import Newsletter from "../../../components/sections/home/newsletter/Newsletter";

const Stores = () => {
  const [stores, setStores] = useState<StoresTypes[]>([]);
  const { t } = useTranslation("translation");
  const [loading, setLoading] = useState(true);
  const { currencyId } = useCurrencies();
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!currencyId) {
      return;
    }
    const getData = async () => {
      try {
        const res = await ProductsService.getALLStoresWithProducts(
          Number(currencyId),
          1,
          20,
        );
        const storesData = res?.data?.data || [];
        setStores(storesData);
        setHasMore(storesData.length === 20);
      } catch (error) {
        console.error("Error loading stores:", error);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [currencyId]);

  const loadMoreStores = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const res = await ProductsService.getALLStoresWithProducts(
        Number(currencyId),
        nextPage,
        20,
      );
      const newData = res?.data?.data || [];
      setStores((prev) => [...prev, ...newData]);
      setPage(nextPage);
      setHasMore(newData.length === 20);
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  const getStoreInitial = (name: string) => {
    return name ? name.charAt(0).toUpperCase() : "S";
  };

  return (
    <div className={styles.stores}>
      <TitleUpdater title={t("stores")} />
      <div className="container">
        <h1 className={styles.page_title}>{t("stores")}</h1>
        <div className={styles.content}>
          {stores.length !== 0 ? (
            <>
              <div className={styles.store_grid}>
                {stores.map((store, index) => (
                  <LocalizedLink
                    key={index}
                    to={`/store-products/${store.id}`}
                    className={styles.store_card}
                  >
                    <div className={styles.store_logo}>
                      {store.logo_url ? (
                        <img src={store.logo_url} alt={store.name} />
                      ) : (
                        <div className={styles.store_initial}>
                          {getStoreInitial(store.name)}
                        </div>
                      )}
                    </div>
                    <div className={styles.store_info}>
                      <h2 className={styles.store_name}>{store.name}</h2>
                      {store.description && (
                        <p className={styles.store_description}>
                          {store.description}
                        </p>
                      )}
                      {store.products && store.products.length > 0 && (
                        <p className={styles.product_count}>
                          {store.products.length}{" "}
                          {store.products.length === 1
                            ? t("product")
                            : t("products")}
                        </p>
                      )}
                    </div>
                    <button className={styles.shop_button}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                        />
                      </svg>
                      {t("go_to_store")}
                    </button>
                  </LocalizedLink>
                ))}
              </div>
              {hasMore && (
                <div className={styles.load_more_container}>
                  <Button
                    onClick={loadMoreStores}
                    loading={loadingMore}
                    disabled={loadingMore}
                  >
                    {t("load_more")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className={styles.no_stores}>{t("no_stores")}</p>
          )}
        </div>
        <br />
        <Newsletter />
      </div>
    </div>
  );
};

export default Stores;
